import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, DestroyRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getEnvironment } from '@lx/shared/tokens';
import { ButtonComponent } from '@lx/shared/ui/button';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { DialogModule } from 'primeng/dialog';
import { FocusTrapModule } from 'primeng/focustrap';
import { Sidebar, SidebarModule } from 'primeng/sidebar';
import { Flow, Flows } from '@lx/quote-tool/lite/shared/types';

/**
 * SidePanel is a panel component displayed as an overlay at the edges of the screen.
 *
 * For desktop view, the panel will be displayed on the right side of the screen.
 * For mobile view, the panel will be displayed at the bottom of the screen and in full screen.
 *
 * The body of the dialog will be a template that is passed in.
 */
@Component({
  standalone: true,
  selector: 'lx-side-panel',
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  imports: [DialogModule, SidebarModule, ButtonComponent, CommonModule, FocusTrapModule],
})
export class SidePanelComponent implements OnInit {
  @Input() title = '';
  @Input() visible = false;
  @Input() footerButtonText: string | undefined;
  @Input() footerButtonDisabled = false;
  @Input() size: 'medium' | 'large' = 'medium';
  @Input() spacing = true;

  @Input() activeFlow: Flow = Flows.Adult;
  @Input() autoFocus = false;

  @Output() hiddenPanel = new EventEmitter<void>();
  @Output() footerButtonClicked = new EventEmitter<void>();

  @ViewChild('sidebarElement') sidebarElement: Sidebar | undefined;

  position = 'right';
  fullScreen = false;

  protected readonly Flows = Flows;

  private triggerElement: HTMLElement | null = null;
  private readonly document = inject(DOCUMENT);
  private readonly environment = getEnvironment();
  private readonly breakPointObserverService = inject(BreakpointObserverService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.breakPointObserverService.isMobile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((mobileModeActive) => {
      if (mobileModeActive) {
        this.position = 'bottom';
      } else {
        this.position = this.environment.RTL_ENABLED ? 'left' : 'right';
      }

      this.fullScreen = mobileModeActive;
    });
  }

  onSidebarShow(): void {
    // Store the element that triggered the sidebar for restoration later
    this.triggerElement = this.document.activeElement as HTMLElement;

    // Focus the sidebar itself for immediate keyboard access
    if (this.autoFocus) {
      setTimeout(() => {
        const element = this.sidebarElement?.container;
        if (element) {
          element.setAttribute('tabindex', '-1');
          element.focus();
        }
      }, 150);
    }
  }

  onSidebarHide(): void {
    setTimeout(() => {
      this.triggerElement?.focus();
      this.triggerElement = null;
    }, 100);

    this.hiddenPanel.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscapeKeyDown(event: KeyboardEvent): void {
    if (this.visible && this.sidebarElement) {
      event.preventDefault();
      event.stopPropagation();
      this.sidebarElement.hide();
    }
  }
}
